
import { Loader2, MoreHorizontal, UserCheck, UserMinus, Shield, ShieldOff, Trash2, Key, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface UserActionsDropdownProps {
  isAdmin: boolean;
  isCurrentUser: boolean;
  isAppOwner: boolean;
  showButton: boolean;
  isLoading: boolean;
  isResettingPassword: boolean;
  isUpdatingRole: boolean;
  isUpdatingVerification: boolean;
  isDeletingUser: boolean;
  verificationStatus: 'none' | 'pending' | 'verified' | 'rejected';
  isCreative: boolean;
  onRoleUpdate: () => void;
  onVerificationUpdate: (status: 'none' | 'verified' | 'verified_creative') => void;
  onResetPassword: () => void;
  onDeleteUser: () => void;
}

export function UserActionsDropdown({
  isAdmin,
  isCurrentUser,
  isAppOwner,
  showButton,
  isLoading,
  isResettingPassword,
  isUpdatingRole,
  isUpdatingVerification,
  isDeletingUser,
  verificationStatus,
  isCreative,
  onRoleUpdate,
  onVerificationUpdate,
  onResetPassword,
  onDeleteUser,
}: UserActionsDropdownProps) {
  const getVerificationOptions = () => {
    if (verificationStatus === 'verified') {
      return isCreative ? [
        {
          label: "Remove Verification",
          icon: UserMinus,
          action: () => onVerificationUpdate('none'),
        },
        {
          label: "Make Regular Traveler",
          icon: UserCheck,
          action: () => onVerificationUpdate('verified'),
        }
      ] : [
        {
          label: "Make Creative",
          icon: Star,
          action: () => onVerificationUpdate('verified_creative'),
        },
        {
          label: "Remove Verification",
          icon: UserMinus,
          action: () => onVerificationUpdate('none'),
        }
      ];
    }

    return [
      {
        label: "Verify as Traveler",
        icon: UserCheck,
        action: () => onVerificationUpdate('verified'),
      },
      {
        label: "Verify as Creative",
        icon: Star,
        action: () => onVerificationUpdate('verified_creative'),
      }
    ];
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={isLoading}
          className="h-8 w-8 p-0"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {showButton && (
          <DropdownMenuItem
            onClick={onRoleUpdate}
            disabled={isUpdatingRole}
          >
            {isAdmin ? (
              <>
                <ShieldOff className="mr-2 h-4 w-4" />
                Remove admin
              </>
            ) : (
              <>
                <Shield className="mr-2 h-4 w-4" />
                Make admin
              </>
            )}
          </DropdownMenuItem>
        )}
        
        {getVerificationOptions().map((option, index) => (
          <DropdownMenuItem
            key={index}
            onClick={option.action}
            disabled={isUpdatingVerification}
          >
            <option.icon className="mr-2 h-4 w-4" />
            {option.label}
          </DropdownMenuItem>
        ))}

        {!isCurrentUser && !isAppOwner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onResetPassword}
              disabled={isResettingPassword}
            >
              <Key className="mr-2 h-4 w-4" />
              Reset password
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDeleteUser}
              disabled={isDeletingUser}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete user
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
